import {
  Button,
  DateInput,
  FormContainer,
  FormInput,
  InputFileForm,
  Treturn,
  SwitchInput,
  useFetch,
} from "@4uhub/lib4uhub";
import DataObjectRoundedIcon from "@mui/icons-material/DataObjectRounded";
import { Box, Grid, Stack, Tooltip, Typography } from "@mui/material";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { GlobalAxios } from "../../../../../../axios/axios-config";
import {
  createESignRuleDocument,
  getESignAutomationVariables,
  getESignRuleById,
  getESignRuleDocumentById,
  updateESignRuleDocumentById,
} from "../../../../../../services/esignRule.service";
import { getDocumentSchema, TDocumentForm } from "../esignRuleSchema";
import FormTextEditor from "../../../../../../components/UI/TextEditor/FormTextEditor";
import { AvailableVariablesModal } from "./AvailableVariablesModal";
import { IESignAutomationVariable } from "../../../../../../models/esign-rule";
import {
  ESIGN_AUTOMATION_CODES,
  HEADER_FOOTER_AUTOMATION_CODES,
  WORD_FILE_AUTOMATION_CODES,
} from "./automationCodes";
import { PrescriptionTypesFields } from "./PrescriptionTypesFields";
import { IDocumentsTab } from "./model";

const translationPath = "4uESign.page.automations.automationRules.";
const FILE_ROUTE = process.env.REACT_APP_FILEMANAGER + "/api/v1/Upload";

export const DocumentsTab = memo(
  ({ id, ruleId, esignAutomationCode, onBack }: IDocumentsTab) => {
    const { t } = useTranslation();

    const { sendRequest: save, loading: sLoading } = useFetch(
      createESignRuleDocument,
    );
    const { sendRequest: update, loading: uLoading } = useFetch(
      updateESignRuleDocumentById,
    );
    const { sendRequest: get, loading: gLoading } = useFetch(
      getESignRuleDocumentById,
    );

    const { sendRequest: getVariables, loading: variablesLoading } = useFetch(
      getESignAutomationVariables,
    );
    const { sendRequest: getESignRuleByIdReq } = useFetch(getESignRuleById);
    const [variables, setVariables] = useState<IESignAutomationVariable[]>([]);
    const [hadFileInitially, setHadFileInitially] = useState(false);
    const [originalFileId, setOriginalFileId] = useState<string | undefined>();
    const [automationId, setAutomationId] = useState<string | undefined>();
    const [automationCode, setAutomationCode] = useState<string | undefined>();
    const [variablesOpen, setVariablesOpen] = useState(false);
    const [variablesError, setVariablesError] = useState<string | null>(null);
    const currentAutomationCode = automationCode ?? esignAutomationCode;
    const showHeaderFooterFields = currentAutomationCode
      ? HEADER_FOOTER_AUTOMATION_CODES.includes(currentAutomationCode)
      : false;
    const showWordFileFields = currentAutomationCode
      ? WORD_FILE_AUTOMATION_CODES.includes(currentAutomationCode)
      : false;
    const schema = useMemo(
      () => getDocumentSchema(currentAutomationCode),
      [currentAutomationCode],
    );

    useEffect(() => {
      const fetchAutomationInfo = async () => {
        if (!ruleId) return;

        const ruleResponse = await getESignRuleByIdReq(ruleId);

        if (ruleResponse.success && ruleResponse.data?.esignAutomation) {
          setAutomationId(ruleResponse.data.esignAutomation.id);
          setAutomationCode(ruleResponse.data.esignAutomation.code?.toString());
        }
      };

      fetchAutomationInfo();
    }, [getESignRuleByIdReq, ruleId]);

    const loadVariables = useCallback(async () => {
      if (!automationId) {
        setVariables([]);
        setVariablesError(
          t(translationPath + "variables_automation_not_found") ||
            "Não foi possível identificar a automação vinculada a esta regra.",
        );
        return;
      }

      setVariablesError(null);

      const response = await getVariables({
        EsignAutomationId: automationId,
      });

      if (response.success && response.data) {
        setVariables(response.data);
        return;
      }

      setVariables([]);
      setVariablesError(
        t(translationPath + "variables_load_error") ||
          "Não foi possível carregar as variáveis disponíveis.",
      );
    }, [automationId, getVariables, t]);

    const handleOpenVariables = useCallback(async () => {
      setVariablesOpen(true);
      await loadVariables();
    }, [loadVariables]);

    const handleCloseVariables = useCallback(() => {
      setVariablesOpen(false);
    }, []);

    const saveHandler = (v: TDocumentForm) => {
      const mediaFileIds = v.file ? [v.file.id] : [];

      return save({
        ...v,
        esignRuleId: ruleId,
        mediaFileIds: mediaFileIds,
        deleteMediaFileIds: [],
        headerFooterTypeId: v.headerFooterType?.id ?? null,
        esignRuleDocExtMedCertTypes: v.esignRuleDocExtMedCertTypes.map(
          (item) => ({
            externalId: item.id,
            externalName: item.name,
          }),
        ),
        initialDate: v.initialDate.toISOString().substring(0, 10),
        endDate: v.endDate.toISOString().substring(0, 10),
      });
    };

    const updateHandler = (v: TDocumentForm, id: string) => {
      let mediaFileIds: string[] = [];
      let deleteMediaFileIds: string[] = [];

      if (v.file) {
        if (v.file.id !== originalFileId) {
          mediaFileIds = [v.file.id];
        }
      } else {
        if (hadFileInitially) {
          deleteMediaFileIds = [id];
        }
      }

      return update({
        item: {
          ...v,
          id: id,
          mediaFileIds: mediaFileIds,
          deleteMediaFileIds: deleteMediaFileIds,
          headerFooterTypeId: v.headerFooterType?.id ?? null,
          esignRuleDocExtMedCertTypes: v.esignRuleDocExtMedCertTypes.map(
            (item) => ({
              externalId: item.id,
              externalName: item.name,
            }),
          ),
          initialDate: v.initialDate.toISOString().substring(0, 10),
          endDate: v.endDate.toISOString().substring(0, 10),
        },
        id: id,
      });
    };

    const getHandler = useCallback(
      async (id: string): Promise<Treturn<TDocumentForm>> => {
        const { data, success } = await get(id);
        if (data && success) {
          const hasFile = !!data.fileName;
          setHadFileInitially(hasFile);

          const fileId = data.id;
          setOriginalFileId(fileId);

          const formData: TDocumentForm = {
            id: data.id,
            name: data.name,
            order: data.order,
            enable: data.enable,
            headerPdf: data.headerPdf,
            footerPdf: data.footerPdf,
            bodyPdf: data.bodyPdf,
            initialDate: new Date(data.initialDate),
            endDate: new Date(data.endDate),
            file: data.mediaType
              ? { id: data.id, fileName: data.fileName }
              : undefined,
            headerFooterType: data.headerFooterType,
            esignRuleDocExtMedCertTypes: (
              data.esignRuleDocExtMedCertTypes ?? []
            ).map((item) => ({
              id: item.externalId,
              code: item.externalId,
              name: item.externalName,
            })),
          };
          return { data: formData, success: true };
        }
        return { data, success };
      },
      [get],
    );

    const loading = sLoading || uLoading || gLoading;

    return (
      <FormContainer<TDocumentForm>
        saveFunction={saveHandler}
        updateFunction={updateHandler}
        getFunction={getHandler}
        loading={loading}
        title={t(translationPath + "document")}
        schema={schema}
        subtitle={
          id
            ? t(translationPath + "edit_document")
            : t(translationPath + "new_document")
        }
        backButtonOnClick={onBack}
        isModalForm={true}
        redirectOnSave={false}
        onSuccess={onBack}
        propId={id}
        formActionsOnBottom={false}
        defaultValues={{
          enable: true,
          esignRuleDocExtMedCertTypes: [],
        }}
        fields={(_, methods) => (
          <Grid container spacing={2}>
            <Grid item xs={12} textAlign="end">
              <SwitchInput
                name="enable"
                label={t(translationPath + "enable") || "Enable"}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <DateInput
                name="initialDate"
                label={t(translationPath + "initial_date")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DateInput
                name="endDate"
                label={t(translationPath + "end_date")}
              />
            </Grid>

            <Grid item xs={12} sm={8}>
              <FormInput
                name="name"
                label={t(translationPath + "name")}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormInput
                name="order"
                label={t(translationPath + "order")}
                type="number"
                fullWidth
                size="small"
              />
            </Grid>

            {currentAutomationCode === ESIGN_AUTOMATION_CODES.receita && (
              <PrescriptionTypesFields methods={methods} />
            )}

            {showHeaderFooterFields && (
              <>
                <Grid item xs={12}>
                  <Typography variant="caption">
                    {t(translationPath + "header_pdf")}
                  </Typography>
                  <FormTextEditor name="headerPdf" />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption">
                    {t(translationPath + "footer_pdf")}
                  </Typography>
                  <FormTextEditor name="footerPdf" />
                </Grid>
              </>
            )}

            {showWordFileFields && (
              <Grid item xs={12}>
                <Box
                  sx={(theme) => ({
                    px: { xs: 1.5, sm: 2 },
                    py: 1.5,
                    borderRadius: 1.5,
                    border: `1px solid ${theme.palette.divider}`,
                    backgroundColor: theme.palette.background.paper,
                  })}
                >
                  <Stack spacing={1.25}>
                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      spacing={1.5}
                      alignItems={{ xs: "stretch", md: "flex-start" }}
                    >
                      <Box
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          "& .MuiFormHelperText-root": { display: "none" },
                        }}
                      >
                        <InputFileForm
                          name="file"
                          axios={GlobalAxios}
                          fileRoute={FILE_ROUTE}
                          fileInputProps={{
                            accept:
                              ".docx, .doc, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/msword",
                            label:
                              t(translationPath + "wordFile") || "Upload File",
                            maxFileSize: 5,
                            mediaTypeCode: "1",
                            mediaTypeIdentifier: "EsignRuleDocumentMediaType",
                            parentName: "EsignRuleDocument",
                            type: "Private",
                            inputProps: { size: "small" },
                          }}
                        />
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "stretch",
                          justifyContent: { xs: "flex-start", md: "center" },
                        }}
                      >
                        <Tooltip
                          title={
                            t(
                              translationPath + "available_variables_tooltip",
                            ) || "Visualizar variáveis disponíveis"
                          }
                        >
                          <span>
                            <Button
                              variant="outlined"
                              color="inherit"
                              startIcon={
                                <DataObjectRoundedIcon fontSize="small" />
                              }
                              onClick={handleOpenVariables}
                              disabled={!automationId}
                              sx={{
                                minWidth: { xs: "100%", md: 148 },
                                height: 40,
                                px: 1.5,
                                whiteSpace: "nowrap",
                                borderColor: "divider",
                              }}
                            >
                              {t(
                                translationPath + "available_variables_button",
                              ) || "Variáveis"}
                            </Button>
                          </span>
                        </Tooltip>
                      </Box>
                    </Stack>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ pl: 1.5, display: "block" }}
                    >
                      {t("Lib4uhub.components.inputFile.accept")} .docx, .doc
                    </Typography>
                  </Stack>
                </Box>
              </Grid>
            )}

            <AvailableVariablesModal
              open={variablesOpen}
              loading={variablesLoading}
              error={variablesError}
              variables={variables}
              onClose={handleCloseVariables}
              onRetry={loadVariables}
            />
          </Grid>
        )}
      />
    );
  },
);
